import { c as _c } from "react/compiler-runtime";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@clerk/nextjs';
import { ApiClient } from '@/services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TopBarNotificationType = 'error' | 'warning' | 'info';
export type TopBarNotification = {
  id: string;
  type: TopBarNotificationType;
  /** The main message displayed in the bar. */
  message: string;
  /** Explicit numeric priority — lower = more important. */
  priority: number;
  /** Optional link rendered as a CTA next to the message. */
  link?: {
    label: string;
    href: string;
  };
};

// ---------------------------------------------------------------------------
// Notification registry — single source of truth for all TopBar alerts
//
// Priority order (lower = shown first):
//   0  Subscription expiring / canceled
//   1  Credits depleted
//   2  WhatsApp profile banned
//   3  WhatsApp payment failure
//   6  Instagram account disconnected
//   7  WhatsApp profile inactive
//   9  Instagram account paused
//  10  No active sequences for Instagram account
//  11  Story replies / comments ignored (no sequence matched them)
//  12  Lead processing failed (no sequence / deleted WhatsApp profile)
//  13  Lead processing failed (no WhatsApp phone)
//  14  Lead processing failed (insufficient balance)
//  15  Lead processing failed (rate limited)
//  16  Lead processing failed (other)
// ---------------------------------------------------------------------------

export const TOPBAR_NOTIFICATIONS = {
  // ── Billing ───────────────────────────────────────────────────────────
  subscriptionExpiring: {
    id: 'subscription-expiring',
    type: 'error' as const,
    priority: 0,
    messageKey: 'topbar.subscriptionExpiring',
    linkLabelKey: 'topbar.manageBilling',
    linkHref: '/settings/workspace/billing'
  },
  lowCredits: {
    id: 'low-credits',
    type: 'error' as const,
    priority: 1,
    messageKey: 'topbar.lowCredits',
    linkLabelKey: 'topbar.topUpNow',
    linkHref: '/settings/workspace/billing?topup=true'
  },
  // ── WhatsApp ──────────────────────────────────────────────────────────
  whatsappBanned: {
    id: 'whatsapp-banned',
    type: 'error' as const,
    priority: 2,
    messageKey: 'topbar.whatsappBanned',
    linkLabelKey: 'topbar.viewWhatsappProfile',
    linkHref: '/integrations/whatsapp'
  },
  whatsappPaymentFailure: {
    id: 'whatsapp-payment-failure',
    type: 'error' as const,
    priority: 3,
    messageKey: 'topbar.whatsappPaymentFailure',
    linkLabelKey: 'topbar.viewWhatsappProfile',
    linkHref: '/integrations/whatsapp'
  },
  whatsappInactive: {
    id: 'whatsapp-inactive',
    type: 'warning' as const,
    priority: 7,
    messageKey: 'topbar.whatsappInactive',
    linkLabelKey: 'topbar.viewWhatsappProfile',
    linkHref: '/integrations/whatsapp'
  },
  // Personal WhatsApp (QR) session dropped: as blocking as a ban, since every
  // outbound message fails until the user rescans.
  personalWhatsappDisconnected: {
    id: 'personal-whatsapp-disconnected',
    type: 'error' as const,
    priority: 2,
    messageKey: 'topbar.personalWhatsappDisconnected',
    linkLabelKey: 'topbar.reconnectWhatsapp',
    linkHref: '/integrations/personal-whatsapp'
  },
  // ── Instagram DM ──────────────────────────────────────────────────────
  instagramDisconnected: {
    id: 'instagram-disconnected',
    type: 'error' as const,
    priority: 6,
    messageKey: 'topbar.instagramDisconnected',
    linkLabelKey: 'topbar.viewInstagram',
    linkHref: '/integrations/instagram/manage'
  },
  instagramPaused: {
    id: 'instagram-paused',
    type: 'warning' as const,
    priority: 9,
    messageKey: 'topbar.instagramPaused',
    linkLabelKey: 'topbar.viewInstagram',
    linkHref: '/integrations/instagram/manage'
  },
  // ── No active sequences ───────────────────────────────────────────────
  noActiveSequences: {
    id: 'no-active-sequences',
    type: 'warning' as const,
    priority: 10,
    messageKey: 'topbar.noActiveSequences',
    linkLabelKey: 'topbar.viewSequences',
    linkHref: '/sequences'
  },
  // ── Inbound events nobody could answer ────────────────────────────────
  // A story reply / comment that matches no sequence is dropped silently:
  // no lead, no conversation, no failed job. These say it out loud.
  storyRepliesIgnored: {
    id: 'story-replies-ignored',
    type: 'warning' as const,
    priority: 11,
    messageKey: 'topbar.storyRepliesIgnored',
    linkLabelKey: 'topbar.viewSequences',
    linkHref: '/sequences'
  },
  commentsIgnored: {
    id: 'comments-ignored',
    type: 'warning' as const,
    priority: 11,
    messageKey: 'topbar.commentsIgnored',
    linkLabelKey: 'topbar.viewSequences',
    linkHref: '/sequences'
  },
  // ── Lead processing failures ──────────────────────────────────────────
  leadFailedNoSequence: {
    id: 'lead-failed-no-sequence',
    type: 'error' as const,
    priority: 12,
    messageKey: 'topbar.leadFailedNoSequence',
    linkLabelKey: 'topbar.viewSequences',
    linkHref: '/sequences'
  },
  leadFailedNoWhatsappProfile: {
    id: 'lead-failed-no-whatsapp-profile',
    type: 'error' as const,
    priority: 12,
    messageKey: 'topbar.leadFailedNoWhatsappProfile',
    linkLabelKey: 'topbar.viewSequences',
    linkHref: '/sequences'
  },
  leadFailedNoWhatsappPhone: {
    id: 'lead-failed-no-whatsapp-phone',
    type: 'error' as const,
    priority: 13,
    messageKey: 'topbar.leadFailedNoWhatsappPhone',
    linkLabelKey: 'topbar.viewWhatsapp',
    linkHref: '/integrations/whatsapp'
  },
  leadFailedPaymentFailure: {
    id: 'lead-failed-payment-failure',
    type: 'error' as const,
    priority: 14,
    messageKey: 'topbar.leadFailedPaymentFailure',
    linkLabelKey: 'topbar.viewWhatsapp',
    linkHref: '/integrations/whatsapp'
  },
  leadFailedRateLimit: {
    id: 'lead-failed-rate-limit',
    type: 'warning' as const,
    priority: 15,
    messageKey: 'topbar.leadFailedRateLimit',
    linkLabelKey: 'topbar.viewWhatsappProfile',
    linkHref: '/integrations/whatsapp'
  }
} as const;
export type TopBarNotificationKey = keyof typeof TOPBAR_NOTIFICATIONS;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_VISIBLE = 2;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

type TopBarContextProps = {
  visibleNotifications: TopBarNotification[];
  queuedCount: number;
  pushNotification: (notification: TopBarNotification) => void;
  removeNotification: (id: string) => void;
  /** Remove all notifications whose id starts with the given prefix. */
  removeNotificationsByPrefix: (prefix: string) => void;
  dismissNotification: (id: string) => void;
};
const TopBarContext = createContext<TopBarContextProps>({
  visibleNotifications: [],
  queuedCount: 0,
  pushNotification: () => {},
  removeNotification: () => {},
  removeNotificationsByPrefix: () => {},
  dismissNotification: () => {}
});
type TopBarProviderProps = {
  children: ReactNode;
};
const TopBarProvider = t0 => {
  const $ = _c(40);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [notifications, setNotifications] = useState(t1);
  const queryClient = useQueryClient();
  const {
    organization
  } = useOrganization();
  organization?.id;
  const t2 = organization?.id;
  let t3;
  if ($[1] !== t2) {
    t3 = ["organization.dismissedNotifications", t2];
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const DISMISSED_QUERY_KEY = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setNotifications([]);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = organization?.id;
  let t6;
  if ($[4] !== t5) {
    t6 = [t5];
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  useEffect(t4, t6);
  const t7 = !!organization?.id;
  let t8;
  if ($[6] !== DISMISSED_QUERY_KEY || $[7] !== t7) {
    t8 = {
      queryKey: DISMISSED_QUERY_KEY,
      queryFn: _temp,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      enabled: t7
    };
    $[6] = DISMISSED_QUERY_KEY;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const {
    data: dismissedRes,
    isFetched: dismissedLoaded
  } = useQuery(t8);
  let t9;
  if ($[9] !== dismissedRes?.data?.DismissedNotifications) {
    t9 = dismissedRes?.data?.DismissedNotifications ?? [];
    $[9] = dismissedRes?.data?.DismissedNotifications;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const dismissedIds = t9;
  let t10;
  if ($[11] !== dismissedIds || $[12] !== dismissedLoaded) {
    t10 = notification => {
      if (!dismissedLoaded) {
        return;
      }
      if (dismissedIds.includes(notification.id)) {
        return;
      }
      setNotifications(prev => {
        if (prev.some(n => n.id === notification.id)) {
          return prev;
        }
        return [...prev, notification];
      });
    };
    $[11] = dismissedIds;
    $[12] = dismissedLoaded;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const pushNotification = t10;
  let t11;
  if ($[14] !== DISMISSED_QUERY_KEY || $[15] !== dismissedIds || $[16] !== queryClient) {
    t11 = id => {
      setNotifications(prev_0 => prev_0.filter(n_0 => n_0.id !== id));
      if (dismissedIds.includes(id)) {
        queryClient.setQueryData(DISMISSED_QUERY_KEY, old => {
          const current = old?.data?.DismissedNotifications ?? [];
          return {
            ...old,
            data: {
              DismissedNotifications: current.filter(d => d !== id)
            }
          };
        });
        ApiClient.organization.undismissNotification(id).then(() => {
          queryClient.invalidateQueries({
            queryKey: DISMISSED_QUERY_KEY
          });
        });
      }
    };
    $[14] = DISMISSED_QUERY_KEY;
    $[15] = dismissedIds;
    $[16] = queryClient;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const removeNotification = t11;
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = prefix => {
      setNotifications(prev_1 => {
        const next = prev_1.filter(n_1 => !n_1.id.startsWith(prefix));
        return next.length === prev_1.length ? prev_1 : next;
      });
    };
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const removeNotificationsByPrefix = t12;
  let t13;
  if ($[19] !== DISMISSED_QUERY_KEY || $[20] !== queryClient) {
    t13 = id_0 => {
      setNotifications(prev_2 => prev_2.filter(n_2 => n_2.id !== id_0));
      queryClient.setQueryData(DISMISSED_QUERY_KEY, old_0 => {
        const current_0 = old_0?.data?.DismissedNotifications ?? [];
        if (current_0.includes(id_0)) {
          return old_0;
        }
        return {
          ...old_0,
          data: {
            DismissedNotifications: [...current_0, id_0]
          }
        };
      });
      ApiClient.organization.dismissNotification(id_0).then(() => {
        queryClient.invalidateQueries({
          queryKey: DISMISSED_QUERY_KEY
        });
      });
    };
    $[19] = DISMISSED_QUERY_KEY;
    $[20] = queryClient;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const dismissNotification = t13;
  let t14;
  if ($[22] !== dismissedIds || $[23] !== notifications) {
    let t15;
    if ($[25] !== dismissedIds) {
      t15 = n_3 => !dismissedIds.includes(n_3.id);
      $[25] = dismissedIds;
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    t14 = notifications.filter(t15);
    $[22] = dismissedIds;
    $[23] = notifications;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const active = t14;
  let t15;
  if ($[27] !== active) {
    t15 = [...active].sort(_temp2);
    $[27] = active;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const sorted = t15;
  let t16;
  if ($[29] !== sorted) {
    t16 = sorted.slice(0, MAX_VISIBLE);
    $[29] = sorted;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const visibleNotifications = t16;
  const queuedCount = Math.max(0, sorted.length - MAX_VISIBLE);
  let t17;
  if ($[31] !== dismissNotification || $[32] !== pushNotification || $[33] !== queuedCount || $[34] !== removeNotification || $[35] !== visibleNotifications) {
    t17 = {
      visibleNotifications,
      queuedCount,
      pushNotification,
      removeNotification,
      removeNotificationsByPrefix,
      dismissNotification
    };
    $[31] = dismissNotification;
    $[32] = pushNotification;
    $[33] = queuedCount;
    $[34] = removeNotification;
    $[35] = visibleNotifications;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== children || $[38] !== t17) {
    t18 = <TopBarContext.Provider value={t17}>{children}</TopBarContext.Provider>;
    $[37] = children;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  return t18;
};
const useTopBar = () => {
  return useContext(TopBarContext);
};
export { TopBarProvider, useTopBar };
function _temp() {
  return ApiClient.organization.getDismissedNotifications();
}
function _temp2(a, b) {
  return a.priority - b.priority;
}